<script setup lang="ts">
import { getUsersByTenantId } from "../../../api/services/Frontegg";
import { useAuthStateStore } from "../../../stores/Utils/auth/AuthState";
import { computed, onMounted, ref } from "vue";
import User from "../../../types/auth/User";
import { useToast } from "vue-toastification";
import { isAxiosError } from "axios";
import {
  inviteUserToBuildingProject,
  getBuildingProjectUserList,
  uninviteUserFromBuildingProject,
  updateUserRole,
} from "../../../api/services/BuildingProject";
import Modal from "../../Reusables/Modal/Modal.vue";
import Button from "../../Reusables/Buttons/Button.vue";
import SelectInputNew from "../../Reusables/Inputs/SelectInputNew.vue";
import SearchSelectUser from "./SearchSelectUser.vue";
import UserAlreadyInProject from "./UserAlreadyInProject.vue";
import { useMemberSpeckleStatus, speckleInviteWarning } from "./useMemberSpeckleStatus.composable";
import usePermissions from "../../../providers/Permissions/PermissionsProvider";
import { NewBuildingProjectType, BuildingProjectType, UserInProjectType } from "@/features/BuildingProject";
import { storeToRefs } from "pinia";
import { OverviewBuildingProjectType } from "@/_to_be_deleted_soon/ProjectList";

const props = defineProps<{
  project: BuildingProjectType | NewBuildingProjectType | OverviewBuildingProjectType;
  buildingProjectId: string;
  userPermissions: Array<string>;
}>();

const emits = defineEmits(["toggleInviteModal"]);

const toggleView = () => {
  emits("toggleInviteModal");
};

const authState = useAuthStateStore();
const { user } = storeToRefs(authState);
const usersAlreadyInProject = ref<Array<UserInProjectType>>([]);
const allUsers = ref<Array<User>>([]);
const users = ref<Array<User>>([]);
const selectedUser = ref<User>();
const roles = ref<Array<string>>(["Admin", "Contributor", "Viewer"]);
const selectedRole = ref<string | null>(null);
const isInviting = ref<boolean>(false);
const userListLoaded = ref<boolean>(false);
const tenantId = ref<string>();
const updateList = ref<number>(0);

const selfIsContributor = ref<boolean>(false);
const selfIsAdmin = ref<boolean>(false);

// A new project carries the stream at the top level; the REST and overview shapes nest it under speckleData.
const streamId = computed(() => {
  const project = props.project;
  const topLevel = "streamId" in project ? project.streamId : undefined;
  return topLevel || ("speckleData" in project ? project.speckleData?.streamId : undefined) || null;
});
const memberSpeckleStatus = useMemberSpeckleStatus(streamId, usersAlreadyInProject, () => user.value?.userId);

onMounted(async () => {
  tenantId.value = user.value?.tenantId;
  if (tenantId.value === undefined) return;

  if (usePermissions().isAdmin(props.userPermissions)) {
    selfIsAdmin.value = true;
  } else if (usePermissions().isContributer(props.userPermissions)) {
    selfIsContributor.value = true;
  }

  if (!selfIsAdmin.value) {
    roles.value.splice(0, 1);
  }

  initializeUsers(tenantId.value);
});

const initializeUsers = async (tenantId: string) => {
  const response = await getUsersByTenantId(tenantId);
  allUsers.value = JSON.parse(JSON.stringify(response.items));
  users.value = response.items.sort((a: User, b: User) => (a.name > b.name ? 1 : -1));
  usersAlreadyInProject.value = await getBuildingProjectUserList(props.buildingProjectId);

  usersAlreadyInProject.value.map((userAlreadyInProject) => {
    userAlreadyInProject.dropdownIsShown = false;
  });

  removeUsersFromListIfAlreadyPartOfProject();
  if (streamId.value) void memberSpeckleStatus.refresh();
};

const removeUsersFromListIfAlreadyPartOfProject = () => {
  usersAlreadyInProject.value.forEach((userAlreadyInProject) => {
    if (!users.value.find((usr) => usr.email === userAlreadyInProject.email)) return;

    users.value.splice(
      users.value.findIndex((usr) => usr.email === userAlreadyInProject.email),
      1
    );
  });
  userListLoaded.value = true;
};

const inviteUser = async () => {
  if (!selectedUser.value || !selectedRole.value) {
    if (!selectedUser.value && !selectedRole.value) useToast().warning("Please select a user and a role");
    if (!selectedUser.value && selectedRole.value) useToast().warning("Please select a user");
    if (!selectedRole.value && selectedUser.value) useToast().warning("Please select a role");
    return;
  }

  isInviting.value = true;

  const roleId =
    selectedRole.value === "Admin"
      ? "project.admin"
      : selectedRole.value === "Contributor"
        ? "project.contributor"
        : "project.viewer";

  const req = {
    inviteeUserId: selectedUser.value.id,
    inviteeEmail: selectedUser.value.email,
    roleId,
  };

  await inviteUserToBuildingProject(req, props.buildingProjectId)
    .then((response) => {
      if (response !== undefined && selectedUser.value) {
        isInviting.value = false;
        useToast().success(`${selectedUser.value?.name} was invited`);
        const invitee: UserInProjectType = {
          email: selectedUser.value.email,
          name: selectedUser.value.name,
          role: selectedRole.value?.toLowerCase() ?? "",
          userId: selectedUser.value.id,
          dropdownIsShown: false,
          speckleStatus: null,
        };
        usersAlreadyInProject.value.push(invitee);
        void memberSpeckleStatus.checkOne(invitee).then((status) => {
          const warning = speckleInviteWarning(status);
          if (warning) useToast().warning(`${invitee.name}: ${warning}`, { timeout: false });
        });

        users.value.splice(
          users.value.findIndex((usr) => usr.id === selectedUser.value?.id),
          1
        );
        selectedUser.value = undefined;
        selectedRole.value = null;
      }
    })
    .catch(() => {
      isInviting.value = false;
      useToast().error(`Something went wrong with while trying to invite ${selectedUser.value?.name}`);
    });
};

// `undefined` when the search field has been edited away from the picked user, so the selection
// can't outlive what the field shows.
const handleSelectedUser = (usr: User | undefined) => {
  selectedUser.value = usr;
};

const handleRemoveUser = async (user: UserInProjectType) => {
  await uninviteUserFromBuildingProject(props.buildingProjectId, user.userId);

  usersAlreadyInProject.value.splice(
    usersAlreadyInProject.value.findIndex((userAlreadyInProject) => userAlreadyInProject.userId === user.userId),
    1
  );

  useToast().warning(`Successfully removed ${user.name} from the project`);
  if (tenantId.value) initializeUsers(tenantId.value);
};

const handleChangeRole = async (user: UserInProjectType, newRole: string) => {
  const roleId = newRole === "admin" ? "project.admin" : newRole === "contributor" ? "project.contributor" : "project.viewer";

  await updateUserRole(props.buildingProjectId, user.userId, roleId)
    .then((response) => {
      if (response !== undefined) {
        user.role = newRole;
        user.dropdownIsShown = false;
        updateList.value++;
        isInviting.value = false;
        useToast().success(`${user.name} was moved to ${newRole}`);
        // Saved, but their Speckle access may not match the new role (RTLCA-5531).
        if (response.speckleWarning) {
          useToast().warning(`${user.name}: ${response.speckleWarning}`, { timeout: false });
        }
      }
    })
    .catch((error) => {
      isInviting.value = false;
      const fallback = `Something went wrong with updating the role for ${user.name}`;
      // A 409 is the refusal for the Speckle model's only owner, with the reason in `message`.
      useToast().error(
        isAxiosError(error) && error.response?.status === 409 ? (error.response?.data?.message ?? fallback) : fallback
      );
    });
};

const getUserImage = (userId: string) => {
  const foundUser = allUsers.value.find((user) => user.id === userId);

  return foundUser?.profilePictureUrl;
};

// Same rule as the settings page's members card (RTLCA-5743): both invite fields are required, so the
// button states that rather than taking the click and warning afterwards.
const canInvite = computed(() => selectedUser.value !== undefined && selectedRole.value !== null);
</script>

<template>
  <Modal
    :heading="`Manage users in ${project.projectName}`"
    size="w-3/4 h-fit"
    @toggle-modal="toggleView">
    <div class="text-type-muted flex flex-col gap-4">
      <div class="border-element-stroke bg-surface rounded-16 border shadow-xs">
        <div class="p-4">
          <span class="text-text-color font-semibold">Add Contributers</span>
        </div>
        <hr />
        <div class="flex gap-4 p-4">
          <div class="w-2/3">
            <SearchSelectUser
              :key="users.length"
              :users="users"
              :loaded="userListLoaded"
              data-cy="user-search"
              @selected-user="handleSelectedUser" />
          </div>
          <div class="w-1/3">
            <SelectInputNew
              v-model="selectedRole"
              label="Role"
              placeholder="Select role"
              data-cy="role-select"
              :required="true">
              <option
                v-for="role in roles"
                :key="role"
                :value="role">
                {{ role }}
              </option>
            </SelectInputNew>
          </div>
          <div class="flex items-end">
            <Button
              :color="canInvite ? 'primary' : 'disabled'"
              :disabled="!canInvite"
              data-cy="invite-btn"
              :label="isInviting ? 'Inviting...' : 'Invite'"
              :is-loading="isInviting"
              @click="inviteUser" />
          </div>
        </div>
      </div>

      <div
        :key="updateList"
        class="flex flex-col gap-4 md:flex-row">
        <div class="border-element-stroke bg-surface rounded-16 h-fit w-full border shadow-xs md:w-1/3">
          <div class="p-4">
            <span class="text-text-color font-semibold">Admins</span>
          </div>

          <hr />
          <div class="p-4 text-xs">
            <span>Admins have full access, including deletion rights.</span>
          </div>

          <div>
            <UserAlreadyInProject
              v-for="projectUser in usersAlreadyInProject.filter((u: UserInProjectType) => u.role === 'admin')"
              :key="projectUser.userId"
              :user="projectUser"
              :self-is-contributer="selfIsContributor"
              :self-is-admin="selfIsAdmin"
              :user-image="getUserImage(projectUser.userId)"
              :is-self="user?.userId === projectUser.userId"
              @remove="handleRemoveUser"
              @change-role="handleChangeRole" />
          </div>
        </div>
        <div class="border-element-stroke bg-surface rounded-16 h-fit w-full border shadow-xs md:w-1/3">
          <div class="p-4">
            <span class="text-text-color font-semibold">Contributers</span>
          </div>

          <hr />
          <div class="p-4 text-xs">
            <span>Contributers can edit the project, but not delete it.</span>
          </div>

          <div>
            <UserAlreadyInProject
              v-for="projectUser in usersAlreadyInProject.filter((u: UserInProjectType) => u.role === 'contributor')"
              :key="projectUser.userId"
              :user="projectUser"
              :self-is-contributer="selfIsContributor"
              :self-is-admin="selfIsAdmin"
              :user-image="getUserImage(projectUser.userId)"
              :is-self="projectUser.userId === user?.userId"
              @remove="handleRemoveUser"
              @change-role="handleChangeRole" />

            <!-- Users -->
          </div>
        </div>
        <div class="border-element-stroke bg-surface rounded-16 h-fit w-full border shadow-xs md:w-1/3">
          <div class="p-4">
            <span class="text-text-color font-semibold">Viewers</span>
          </div>

          <hr />
          <div class="p-4 text-xs">
            <span>Viewers can inspect the dashboard and the mapping view, but cannot change anything.</span>
          </div>

          <div>
            <UserAlreadyInProject
              v-for="projectUser in usersAlreadyInProject.filter(
                (userInProject: UserInProjectType) => userInProject.role === 'viewer'
              )"
              :key="projectUser.userId"
              :user="projectUser"
              :self-is-contributer="selfIsContributor"
              :self-is-admin="selfIsAdmin"
              :user-image="getUserImage(projectUser.userId)"
              :is-self="user?.userId !== projectUser.userId ? false : true"
              @remove="handleRemoveUser"
              @change-role="handleChangeRole" />

            <!-- Users -->
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>
